import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AppShell } from 'components/dashboard/app-shell';
import { useUser } from 'contexts/UserContext';

import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

import { getNote, type Note } from '../services/notes';

function pickErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.detail) return String(data.detail);
  if (err?.message) return String(err.message);
  return fallback;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return format(d, 'dd/MM/yyyy HH:mm', { locale: itLocale });
  } catch {
    return d.toLocaleString();
  }
}

export default function NoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();

  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNote = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await getNote(id);
      setNote(res.data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(pickErrorMessage(err, 'Impossibile caricare la nota.'));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchNote();
  }, [fetchNote]);

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Caricamento...</div>
      </div>
    );
  }

  if (!user && !localStorage.getItem('accessToken')) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell currentUser={user}>
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/notes')}
          >
            <ArrowLeft />
            Torna alle note
          </Button>
        </div>

        {loading ? (
          <DetailSkeleton />
        ) : notFound ? (
          <Alert>
            <AlertTitle>Nota non trovata</AlertTitle>
            <AlertDescription>
              La nota richiesta non esiste o non hai i permessi per
              visualizzarla.
            </AlertDescription>
          </Alert>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTitle>Errore</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : note ? (
          <article>
            <header className="mb-6 border-b pb-4">
              <h1 className="text-2xl font-semibold tracking-tight">
                {note.title || '(senza titolo)'}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Badge
                  variant={note.type === 'meeting' ? 'default' : 'secondary'}
                >
                  {note.type === 'meeting' ? 'meeting' : 'note'}
                </Badge>
                <span>·</span>
                <time dateTime={note.created_at}>
                  {formatDate(note.created_at)}
                </time>
              </div>
            </header>

            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {note.content || ''}
              </ReactMarkdown>
            </div>
          </article>
        ) : null}
      </div>
    </AppShell>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/3" />
      <div className="space-y-2 pt-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
    </div>
  );
}
